import { useCallback, useState } from 'react';

import { defineMessages, useIntl } from 'react-intl';

import { AIAnswerAPIType } from '../../../ai-answer-dialog/ai-answer-api-type';
import { useAIAnswerContext } from '../../../ai-answer-dialog/ai-answer-context';
import type {
	AIAnswerQueryFunctionType,
	SearchAIAnswerQueryVariables,
} from '../../../ai-answer-dialog/types';
import { createAtlAttributionHeader } from '../../../common/fetch/atl-attribution-header';
import { fetchAggResponse } from '../../../common/fetch/fetch-agg-response';
import {
	aiDefinitionsStreamFetchConvoAIService,
	type FetchConfig as FetchConfigType,
} from '../../../stream-api/stream-fetch';
import { fetchCuratedDefinitionErrorPayload } from '../fetch-curated-definition-error-payload';
import { fetchPrecomputedDefinitionErrorPayload } from '../fetch-precomputed-definition-error-payload';
import {
	CURATED_DEFINITION_OPERATION_NAME,
	CuratedDefinitionQuery,
	type CuratedDefinitionQueryType,
	type CuratedDefinitionQueryVariables,
} from '../gql/curated-definition-query';
import {
	PrecomputedDefinitionQuery,
	type PrecomputedDefinitionQueryType,
	type PrecomputedDefinitionQueryVariables,
} from '../gql/precomputed-definition-query';
import { mapCustomDefinitionToNLPSearchType } from './utils';

type TenantArgs = {
	workspaceId: string;
	cloudIdARI: string;
	fetchConfig?: FetchConfigType;
	isDefinitionCurationEnabled?: boolean;
};

type JiraProductArgs = {
	contentId: string;
	projectId: string;
};

type ConfluenceProductArgs = {
	contentId: string;
	spaceId: string;
};

type useDefinitionSupplierArgs = TenantArgs & {
	productConfig: JiraProductArgs | ConfluenceProductArgs;
	product: string;
};

type DefinitionSupplierType = {
	fetchDefinition: AIAnswerQueryFunctionType;
	loading: boolean;
};

const i18n = defineMessages({
	searchAiDialogQueryPretext: {
		id: 'contextual-reading-aids.search-ai-dialog.query-pretext',
		defaultMessage: 'what is {queryText}?',
		description: 'Search query prompt for a highlighted term that a user wants to be defined',
	},
});

const NO_CURATED_DEF_FOUND_MESSAGE = 'No matching scope for Definition';

export const useDefinitionSupplier = ({
	workspaceId,
	cloudIdARI,
	fetchConfig,
	isDefinitionCurationEnabled,
	productConfig,
	product,
}: useDefinitionSupplierArgs): DefinitionSupplierType => {
	const intl = useIntl();
	const [loading, setLoading] = useState(true);
	const { fireAnalyticsEvent, commonAttributes } = useAIAnswerContext();

	const { contentId } = productConfig;

	const fetchAIGeneratedDefinition = useCallback(
		async ({ variables }: { variables: Omit<SearchAIAnswerQueryVariables, 'cloudIdARI'> }) => {
			const { query, locale, additional_context, experience, followUpsEnabled } = variables;

			if (contentId !== '') {
				try {
					const precomputedResult = await fetchAggResponse<
						PrecomputedDefinitionQueryVariables,
						PrecomputedDefinitionQueryType
					>({
						graphQuery: PrecomputedDefinitionQuery,
						variables: {
							workspaceId,
							keyPhrase: variables.query,
							contentId,
						},
						headers: createAtlAttributionHeader({
							atlWorkspaceId: workspaceId,
							tenantId: cloudIdARI,
							product,
						}),
					});

					if (
						precomputedResult.data &&
						!precomputedResult.data.knowledgeDiscovery.autoDefinition.message
					) {
						const data = {
							nlpSearch: {
								...mapCustomDefinitionToNLPSearchType(
									precomputedResult.data.knowledgeDiscovery.autoDefinition,
								),
								extraAPIAnalyticsAttributes: {
									requestId: precomputedResult.extensions?.gateway?.trace_id ?? null,
								},
							},
						};
						setLoading(false);
						return data;
					} else if (
						precomputedResult.data &&
						precomputedResult.data.knowledgeDiscovery.autoDefinition.message
					) {
						throw new Error(precomputedResult.data.knowledgeDiscovery.autoDefinition.message);
					} else {
						throw new Error('Something went wrong with fetching precomputed definition');
					}
				} catch (error) {
					if (
						error instanceof Object &&
						'message' in error &&
						error.message !== NO_CURATED_DEF_FOUND_MESSAGE
					) {
						fireAnalyticsEvent(fetchPrecomputedDefinitionErrorPayload(error, commonAttributes));
					}
				}
				// Fallthrough to fetching the AI generated definition if there is no precomputed definition or the network call fails
			}

			const streamFetchArgs = {
				variables: {
					query: intl.formatMessage(i18n.searchAiDialogQueryPretext, {
						queryText: query,
					}),
					locale,
					cloudIdARI,
					additional_context,
					experience,
					followUpsEnabled,
				},
				isReadingAids: true,
				fetchConfig,
				generatorConfig: {
					yieldAnswerPart: true,
					yieldFinalResponse: true,
				},
				onStreamFinalResponse: () => {
					setLoading(false);
				},
			};
			return {
				type: AIAnswerAPIType.STREAM,
				stream: aiDefinitionsStreamFetchConvoAIService(streamFetchArgs),
			};
		},
		[
			cloudIdARI,
			fetchConfig,
			intl,
			commonAttributes,
			fireAnalyticsEvent,
			workspaceId,
			contentId,
			product,
		],
	);

	const fetchDefinition = useCallback(
		async ({ variables }: { variables: Omit<SearchAIAnswerQueryVariables, 'cloudIdARI'> }) => {
			if (isDefinitionCurationEnabled) {
				try {
					let confluenceScopeId, jiraScopeId;
					if ('spaceId' in productConfig) {
						confluenceScopeId = productConfig;
					} else if ('projectId' in productConfig) {
						jiraScopeId = { projectId: productConfig.projectId };
					}
					const result = await fetchAggResponse<
						CuratedDefinitionQueryVariables,
						CuratedDefinitionQueryType
					>({
						graphQuery: CuratedDefinitionQuery,
						variables: {
							workspaceId,
							keyPhrase: variables.query,
							confluenceScopeId,
							jiraScopeId,
						},
						operationName: CURATED_DEFINITION_OPERATION_NAME,
						headers: createAtlAttributionHeader({
							atlWorkspaceId: workspaceId,
							tenantId: cloudIdARI,
							product,
						}),
					});

					if (result.data && !result.data.knowledgeDiscovery.definition.message) {
						const data = {
							nlpSearch: {
								...mapCustomDefinitionToNLPSearchType(result.data.knowledgeDiscovery.definition),
								extraAPIAnalyticsAttributes: {
									requestId: result.extensions?.gateway?.trace_id ?? null,
								},
							},
						};
						setLoading(false);
						return data;
					} else if (result.data && result.data.knowledgeDiscovery.definition.message) {
						throw new Error(result.data.knowledgeDiscovery.definition.message);
					} else {
						throw new Error('Something went wrong with fetching curated definition');
					}
				} catch (error) {
					// want to fire event when error.message does not equal the no curated definition message
					// returned by the query in data.knowledgeDiscovery.definition.message
					if (
						error instanceof Object &&
						'message' in error &&
						error.message !== NO_CURATED_DEF_FOUND_MESSAGE
					) {
						fireAnalyticsEvent(fetchCuratedDefinitionErrorPayload(error, commonAttributes));
					}
					return fetchAIGeneratedDefinition({ variables });
				}
			}

			return fetchAIGeneratedDefinition({ variables });
		},
		[
			productConfig,
			fetchAIGeneratedDefinition,
			isDefinitionCurationEnabled,
			workspaceId,
			commonAttributes,
			fireAnalyticsEvent,
			cloudIdARI,
			product,
		],
	);

	return { fetchDefinition, loading };
};
